/**
 * A teacher's own booking availability (BON-241) — /api/v1/me/availability.
 * Self-scoped: the API always reads and writes the caller's own rows, so
 * there is no user id in any of these calls.
 *
 * Shapes mirror OwnAvailability in
 * backend/src/services/staff-availability.service.ts — keep in sync.
 */
import { apiFetch } from '../lib/apiFetch';

/** The four coarse slots. `off` means "can't work"; absent means "not set". */
export const AVAILABILITY_SLOTS = ['off', 'daytime', 'evening', 'all_day'] as const;
export type AvailabilitySlot = (typeof AVAILABILITY_SLOTS)[number];

/** Design labels for each slot (availability-teacher.jsx). */
export const SLOT_LABELS: Record<AvailabilitySlot, string> = {
  off: 'Off',
  daytime: 'Daytime',
  evening: 'Evening',
  all_day: 'All day',
};

export interface RhythmEntry {
  /** 0=Sunday … 6=Saturday, matching JS `Date.getUTCDay()`. */
  weekday: number;
  state: AvailabilitySlot;
}

export interface ExceptionEntry {
  /** `YYYY-MM-DD`. */
  date: string;
  state: AvailabilitySlot;
  note: string | null;
}

export interface OwnAvailability {
  rhythm: RhythmEntry[];
  /** Future-dated only — past exceptions are immutable and not returned. */
  exceptions: ExceptionEntry[];
  /** Null until the teacher has saved anything (drives the first-run state). */
  updatedAtIso: string | null;
}

async function parseAvailability(res: Response, fallback: string): Promise<OwnAvailability> {
  let body: { availability?: OwnAvailability; message?: string } | null = null;
  try {
    body = (await res.json()) as { availability?: OwnAvailability; message?: string };
  } catch {
    body = null;
  }
  if (!res.ok || !body?.availability) {
    throw new Error(body?.message ?? `${fallback} (HTTP ${res.status})`);
  }
  return body.availability;
}

export async function fetchOwnAvailability(): Promise<OwnAvailability> {
  const res = await apiFetch('/api/v1/me/availability');
  return parseAvailability(res, 'Failed to load your availability');
}

/** Replace rhythm + future exceptions in full — the "Save availability" action. */
export async function saveOwnAvailability(input: {
  rhythm: RhythmEntry[];
  exceptions: ExceptionEntry[];
}): Promise<OwnAvailability> {
  const res = await apiFetch('/api/v1/me/availability', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return parseAvailability(res, 'Failed to save your availability');
}
